import React from 'react';
import { motion } from 'framer-motion';

const WhoWeAreSection = () => {
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: [0.25, 0.4, 0.25, 1],
      },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const comparisonItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };

  return (
    <section className="relative bg-gradient-to-b from-white via-slate-50/30 to-white py-16 lg:py-20 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-10 w-96 h-96 bg-cyan-100/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-blue-100/20 rounded-full blur-3xl" />
      </div>
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-300/50 to-transparent" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={staggerContainer}
          className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start"
        >
          <motion.div variants={fadeInUp} className="space-y-8">
            <motion.div 
              variants={fadeInUp}
              className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-cyan-600 uppercase"
            >
              <div className="w-8 h-px bg-gradient-to-r from-cyan-600 to-transparent" />
              Who We Are
            </motion.div>

            <motion.h2 
              variants={fadeInUp}
              className="text-4xl lg:text-5xl xl:text-6xl font-extrabold text-slate-900 leading-[1.1] tracking-tight"
            >
              We built OpenbooQ because we{' '}
              <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                lived the problem.
              </span>
            </motion.h2>

            <motion.div 
              variants={fadeInUp}
              className="space-y-6 text-slate-600 text-base lg:text-lg leading-relaxed max-w-2xl"
            >
              <p className="text-slate-700 font-medium">
                We're not outsiders trying to "disrupt" roofing. We owned and operated traditional roofing companies for years. We knocked doors. We sat at kitchen tables. We wrote estimates the way the industry taught us to.
              </p>
              <p className="text-lg font-semibold text-slate-800 italic">
                And over time, we started to see how broken it really was.
              </p>
              <p>
                Pricing varied wildly for the same roof. Homeowners paid for layers of sales commissions, inflated overhead, and unnecessary pressure tactics—without ever seeing where their money actually went. The process wasn't transparent, and it wasn't fair.
              </p>
              <p>
                Even when contractors had good intentions, the system itself pushed bad behavior.
              </p>
              <p className="text-slate-700 font-medium">
                OpenbooQ was built to fix that. We stripped roofing down to what actually matters: material cost, actual labor cost with no markup, and quality workmanship. No markups hidden in fine print. No sales games. Just clear pricing and a better experience for homeowners.
              </p>
            </motion.div>

            <motion.div 
              variants={fadeInUp}
              className="pt-6 border-t border-slate-200"
            >
              <p className="text-sm text-slate-600 italic flex items-start gap-3">
                <span className="text-cyan-600 text-xl flex-shrink-0">"</span>
                <span className="pt-1">We didn't imagine a better way to roof—we built it after years inside the industry.</span>
                <span className="text-cyan-600 text-xl flex-shrink-0 self-end">"</span>
              </p>
            </motion.div>
          </motion.div>

          <motion.div 
            variants={fadeInUp} 
            className="lg:sticky lg:top-32"
          >
            <motion.div 
              className="relative bg-white rounded-3xl p-8 lg:p-10 shadow-2xl border border-slate-200/80 overflow-hidden group"
              whileHover={{ y: -4, transition: { duration: 0.3 } }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/50 via-blue-50/30 to-slate-50/50 opacity-60" />
              
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-cyan-500/10 to-transparent rounded-bl-full" />
              
              <div className="relative z-10">
                <motion.h3 
                  className="text-xl lg:text-2xl font-bold text-slate-900 mb-8 text-center flex items-center justify-center gap-3"
                  variants={fadeInUp}
                >
                  <span className="w-8 h-px bg-gradient-to-r from-transparent to-cyan-600" />
                  What We Changed
                  <span className="w-8 h-px bg-gradient-to-l from-transparent to-cyan-600" />
                </motion.h3>
                
                <motion.div 
                  className="space-y-6"
                  variants={staggerContainer}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  <motion.div 
                    variants={comparisonItemVariants}
                    className="flex items-start gap-4 p-4 rounded-xl bg-white/50 backdrop-blur-sm border border-slate-100 hover:border-cyan-200 hover:shadow-md transition-all duration-300"
                  >
                    <div className="flex-1">
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                        What we saw
                      </div>
                      <div className="text-sm text-slate-500 line-through opacity-70">
                        Sales pressure
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-cyan-600 font-bold text-2xl transform group-hover:translate-x-1 transition-transform duration-300">→</div>
                    <div className="flex-1">
                      <div className="text-[10px] font-bold text-cyan-700 uppercase tracking-wider mb-1.5">
                        What we changed
                      </div>
                      <div className="text-sm font-bold text-slate-900">
                        Transparent pricing
                      </div>
                    </div>
                  </motion.div>

                  <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

                  <motion.div 
                    variants={comparisonItemVariants}
                    className="flex items-start gap-4 p-4 rounded-xl bg-white/50 backdrop-blur-sm border border-slate-100 hover:border-cyan-200 hover:shadow-md transition-all duration-300"
                  >
                    <div className="flex-1">
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                        What we saw
                      </div>
                      <div className="text-sm text-slate-500 line-through opacity-70">
                        Hidden margins
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-cyan-600 font-bold text-2xl transform group-hover:translate-x-1 transition-transform duration-300">→</div>
                    <div className="flex-1">
                      <div className="text-[10px] font-bold text-cyan-700 uppercase tracking-wider mb-1.5">
                        What we changed
                      </div>
                      <div className="text-sm font-bold text-slate-900">
                        Line-item clarity
                      </div>
                    </div>
                  </motion.div>

                  <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

                  <motion.div 
                    variants={comparisonItemVariants}
                    className="flex items-start gap-4 p-4 rounded-xl bg-white/50 backdrop-blur-sm border border-slate-100 hover:border-cyan-200 hover:shadow-md transition-all duration-300"
                  >
                    <div className="flex-1">
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                        What we saw
                      </div>
                      <div className="text-sm text-slate-500 line-through opacity-70">
                        Inconsistent installs
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-cyan-600 font-bold text-2xl transform group-hover:translate-x-1 transition-transform duration-300">→</div>
                    <div className="flex-1">
                      <div className="text-[10px] font-bold text-cyan-700 uppercase tracking-wider mb-1.5">
                        What we changed
                      </div>
                      <div className="text-sm font-bold text-slate-900">
                        Standardized quality
                      </div>
                    </div>
                  </motion.div>
                </motion.div>

                <div className="mt-8 pt-6 border-t border-slate-200">
                  <p className="text-xs text-center text-slate-500 font-medium">
                    Built by roofers, for homeowners
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhoWeAreSection;